import { useQuery } from '@tanstack/react-query';
import type { paths } from '@bastion/api';
import { authenticatedFetch } from '../authenticatedFetch';
import type { DatasetRow } from '../types';

/**
 * The wire types come from the server's OpenAPI schema, so a route change
 * fails the type check here. Regenerate them with
 * `npm run --workspace=@bastion/api generate-types`.
 */
type DatasetListResponse =
  paths['/datasets']['get']['responses']['200']['content']['application/json'];
type DatasetListQuery = NonNullable<paths['/datasets']['get']['parameters']['query']>;
type DatasetDetailResponse =
  paths['/datasets/{dataset_id}']['get']['responses']['200']['content']['application/json'];

export interface DatasetsResult {
  data: DatasetRow[];
  count: number;
}

export interface DatasetQueryFilters {
  task?: string;
  search?: string;
  sort_by?: 'created_at' | 'name' | 'episode_count';
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/** Every key is a query parameter the route declares, or the check fails. */
const DATASET_LIST_KEYS = [
  'task',
  'search',
  'sort_by',
  'sort_order',
  'limit',
  'offset',
] as const satisfies readonly (keyof DatasetQueryFilters & keyof DatasetListQuery)[];

function buildListPath(filters: DatasetQueryFilters): string {
  const params = new URLSearchParams();
  for (const key of DATASET_LIST_KEYS) {
    const value = filters[key];
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `/datasets?${search}` : '/datasets';
}

/**
 * List datasets. The server applies the filters and counts the datasets they
 * match, so `count` is the number of matches and not the size of `data`.
 *
 * The server defaults to the newest datasets first, and to a page of 100.
 */
export function useDatasets(filters: DatasetQueryFilters = {}) {
  return useQuery({
    queryKey: ['datasets', filters],
    queryFn: async ({ signal }): Promise<DatasetsResult> => {
      const resp = await authenticatedFetch<DatasetListResponse>(buildListPath(filters), {
        signal,
      });
      return { data: resp.items, count: resp.total };
    },
  });
}

/**
 * Fetch one dataset by id.
 *
 * The route also returns the dataset's whole episode list. The result type is
 * `DatasetRow`, so a caller reads the dataset fields only, and a caller that
 * needs the episodes uses `useDatasetEpisodes`, which pages them. A large
 * dataset therefore costs a large response here; narrow the route if that
 * becomes a problem.
 */
export function useDatasetById(datasetId: string | null) {
  return useQuery({
    queryKey: ['dataset', datasetId],
    queryFn: async ({ signal }): Promise<DatasetRow | null> => {
      if (!datasetId) return null;
      const resp = await authenticatedFetch<DatasetDetailResponse>(
        `/datasets/${encodeURIComponent(datasetId)}`,
        { signal },
      );
      // Drop the episode list, so the cached object holds the fields
      // DatasetRow declares and nothing else.
      const { episodes: _episodes, ...row } = resp;
      return row;
    },
    enabled: !!datasetId,
  });
}
